<template>
    <form @submit.prevent="review.post(requestUrl, {
        preserveScroll: true,
    })">
        <app-title class="mt-4"> Leave a tyre review </app-title>

        <p class="mt-4 text-dark">
            Thank you for taking time out of your day to review your tyres.
        </p>

        <p class="mt-2 text-dark">
            By leaving your review, you will help tyre researchers across
            the world that visit
            <Link href="/" class="font-bold">{{ siteName }}</Link>
            every month help find the right tyres for their car!
        </p>

        <!-- Step 1 - The tyre to review -->
        <app-title class="mt-8" type="subtitle">1. Your Tyres</app-title>

        <review-user-tyres
            v-model:product="localProduct"
            v-model:brand="localBrand"
            v-model:width="review.tyre_details.width"
            v-model:profile="review.tyre_details.profile"
            v-model:rim="review.tyre_details.rim"
            v-model:miles="review.tyre_details.miles"
            v-model:drivingStyle="review.tyre_details.driving_style"
            v-model:roadType="review.tyre_details.road_type"
            v-model:tyreAge="review.tyre_details.tyre_age"
            :allow-product-edit="allowProductEdit"
            :errors="review.errors"
        ></review-user-tyres>

        <!-- Step 2 - Scores for questions -->
        <app-title type="subtitle" class="mt-8"> 2. Your scores </app-title>

        <p class="mt-4 text-dark">
            Please score the tyres qualities between 1 and 10, with 10 being
            the best score and 1 the worst.
        </p>

        <p class="mt-2 text-dark">
            If you'd like to skip a section, just leave the selection as
            "N/A".
        </p>

        <review-question
            v-for="(question, key) of questions"
            :key="key"
            class="mt-4"
            :question="question"
            v-model="review.questions[key].answer"
            :error="review.errors[`questions.${key}.answer`]"
        ></review-question>

        <!-- Step 3 - Your thoughts -->
        <app-title type="subtitle" class="mt-8"> 3. Your thoughts </app-title>

        <p class="mt-4 text-dark">
            Please try and be as detailed as possible. An ideal review will
            contain information about how you found the dry and wet grip,
            along with the wear rate and comfort of the tyre compared to any
            other tyres you've used on your car.
        </p>

        <input-textarea
            class="mt-4"
            v-model="review.comment"
            :error="review.errors.comment"
        ></input-textarea>

        <input-error v-if="review.errors.comment">{{ review.errors.comment }}</input-error>

        <!-- Step 4 - Your car -->
        <app-title type="subtitle" class="mt-8"> 4. Your car </app-title>

        <review-user-car
            v-model:manufacturer="review.tyre_details.vehicle_manufacturer"
            v-model:model="review.tyre_details.vehicle_model"
            v-model:manufacturerNotFound="review.tyre_details.manufacturer_not_found"
            :errors="review.errors"
        ></review-user-car>

        <!-- Step 5 - Your driving style -->
        <app-title type="subtitle" class="mt-8">
            5. Your driving style
        </app-title>

        <review-driving-style
            v-model:roadType="review.tyre_details.road_type"
            v-model:drivingStyle="review.tyre_details.driving_style"
            :errors="review.errors"
        ></review-driving-style>

        <!-- Step 6 - Your details -->
        <!-- Only show if we haven't already got user -->
        <template v-if="!user || !user.email || !user.name" >
            <app-title type="subtitle" class="mt-8"> 6. Your details</app-title>

            <review-user-details
                v-model:username="review.user.name"
                v-model:email="review.user.email"
                v-model:marketing="review.user.marketing"
                type="fitter"
                :errors="review.errors"
            ></review-user-details>
        </template>

        <template v-if="website">
            <input-select class="mt-4"
                          v-model="review.referred_from"
                          :options="referralTypes"
                          :placeholder="`Where did you hear about ` + ucwords(website.name) + ' ?'"></input-select>

            <input-error class="mt-1" :error="review.errors.referred_from"></input-error>
        </template>

        <input-recaptcha class="mt-4" v-model="review.recaptcha" ref="recaptcha"></input-recaptcha>

        <input-error class="mt-1" v-if="review.errors.recaptcha">
            {{ review.errors.recaptcha }}
        </input-error>

        <div>
            <app-button type="submit" class="mt-4" :loading="review.processing">Submit your review</app-button>
        </div>
    </form>
</template>
<script>
import Strings from "@/Mixins/Utilities/Strings";
import {Link, useForm, usePage} from "@inertiajs/inertia-vue3";
import AppNavbar from "@/Components/AppNavbar.vue";
import AppTitle from "@/Components/AppTitle.vue";
import ReviewUserTyres from "@/Components/Reviews/Forms/ReviewUserTyres.vue";
import ReviewQuestion from "@/Components/Reviews/Forms/ReviewQuestion.vue";
import ReviewUserCar from "@/Components/Reviews/Forms/ReviewUserCar.vue";
import ReviewDrivingStyle from "@/Components/Reviews/Forms/ReviewDrivingStyle.vue";
import ReviewUserDetails from "@/Components/Reviews/Forms/ReviewUserDetails.vue";
import InputTextarea from "@/Components/Forms/Inputs/InputTextarea.vue";
import InputSelect from "@/Components/Forms/Inputs/InputSelect.vue";
import InputRecaptcha from "@/Components/Forms/Inputs/InputRecaptcha.vue";
import InputError from "@/Components/Forms/Inputs/InputError.vue";
import AppButton from "@/Components/AppButton.vue";
import AppPoweredBy from "@/Components/AppPoweredBy.vue";
import AppCard from "@/Components/AppCard.vue";

export default {
    mixins: [Strings],
    components: {
        AppNavbar,
        AppTitle,
        Link,
        ReviewUserTyres,
        ReviewQuestion,
        InputTextarea,
        InputSelect,
        InputError,
        ReviewUserDetails,
        AppButton,
        ReviewUserCar,
        ReviewDrivingStyle,
        AppPoweredBy,
        AppCard,
        InputRecaptcha
    },
    props: {
        product: Object,
        questions: Array,
        website: Object,
        user: Object,
        tyreDetails: Object,
        rating: [Number, String],
        requestUrl: {
            type: String,
            default: "/reviews"
        },
        allowProductEdit: {
            default: true,
            type: Boolean
        },
        orderReference: String,
        referralTypes: Array
    },
    data() {
        const questions = this.questions.map((question) => {
            return {
                question_id: question.id,
                answer: question.required ? 10 : null,
                required: question.required
            };
        });

        return {
            review: useForm({
                brand_id: this.product ? this.product.brand.id : null,
                product_id: this.product ? this.product.id : null,
                website_id: this.website ? this.website.id : null,
                questions: questions,
                comment: "",
                order_reference: this.orderReference,
                user: {
                    name: this.user?.name ?? "",
                    email: this.user?.email ?? "",
                    marketing: false,
                },
                recaptcha: null,
                referred_from: null,
                tyre_details: {
                    width: this.tyreDetails?.width ?? null,
                    profile: this.tyreDetails?.profile ?? null,
                    rim: this.tyreDetails?.rim ?? null,
                    miles: null,
                    tyre_age: null,
                    road_type: null,
                    driving_style: null,
                    vehicle_manufacturer: null,
                    vehicle_model: null,
                    vehicle_manufacturer_id: null,
                    vehicle_model_id: null,
                    manufacturer_not_found: false
                },
            }),
            localBrand: this.product ? this.product.brand : null,
            localProduct: this.product ?? null,
        };
    },
    computed: {
        siteName() {
            return usePage().props.value.config.name;
        }
    },
    methods: {
        verifyCaptcha(response) {
            this.review.recaptcha = response;
        },
    },
    watch: {
        "review.errors"() {
            this.$refs.recaptcha.reset();
        },
        localBrand(brand) {
            this.review.brand_id = brand.id;
        },
        localProduct(product) {
            this.review.product_id = product.id;
        },
        "review.tyre_details.vehicle_manufacturer"(manufacturer) {
            this.review.tyre_details.vehicle_manufacturer_id = manufacturer?.id ?? null;
        },
        "review.tyre_details.vehicle_model"(model) {
            this.review.tyre_details.vehicle_model_id = model?.id ?? null;
        }
    }
};
</script>


<style lang="scss" scoped>

</style>
